/*
 * Copyright (c) 2022. Nedaware SL
 */

import {JsonLdInterface} from './entity-interface';

export interface CollectionAnswerInterface<T> {
    'hydra:totalItems': number;
    'hydra:member': T[];
    'hydra:view'?: CollectionAnswerViewInterface;
}

export interface CollectionAnswerViewInterface extends JsonLdInterface {
    /** Iri de la primera página */
    'hydra:first'?: string;
    /** Iri de la última página */
    'hydra:last'?: string;
    /** Iri de la siguiente página */
    'hydra:next'?: string;
    /** Iri de la página anterior */
    'hydra:previous'?: string;
}

export class Paginated<T> implements Iterable<T> {
    public readonly member: T[];
    public readonly totalItems: number;
    public readonly currentPage?: number;
    public readonly lastPage?: number;
    public readonly iriCurrentPage?: string;
    public readonly iriFirstPage?: string;
    public readonly iriLastPage?: string;
    public readonly iriNextPage?: string;
    public readonly iriPrevPage?: string;

    constructor(paginatedObject: CollectionAnswerInterface<T>) {
        this.totalItems = paginatedObject['hydra:totalItems'];
        this.member = paginatedObject['hydra:member'];
        const view = paginatedObject['hydra:view'];
        if (view) {
            this.iriCurrentPage = view['@id'];
            this.iriFirstPage = view['hydra:first'];
            this.iriLastPage = view['hydra:last'];
            this.iriNextPage = view['hydra:next'];
            this.iriPrevPage = view['hydra:previous'];
            if (this.iriCurrentPage) {
                this.currentPage = Paginated.extractPage(this.iriCurrentPage);
            }
            if (this.iriLastPage) {
                this.lastPage = Paginated.extractPage(this.iriLastPage);
            }
        }
    }

    public static fromArray<T>(arr: Array<T>): Paginated<T> {
        return new Paginated<T>({
            'hydra:totalItems': arr.length,
            'hydra:member': arr
        });
    }

    public static isNotPaginatedAnswerInterface(obj: any): boolean {
        return !obj.hasOwnProperty('hydra:totalItems') ||
            !obj.hasOwnProperty('hydra:member');
    }

    public static mightBePaginatedAnswerInterface(obj: any): boolean {
        return obj.hasOwnProperty('hydra:totalItems') &&
            obj.hasOwnProperty('hydra:member');
    }

    private static extractPage(iri: string): number {
        const paramsStart = iri.indexOf('?');
        if (paramsStart >= 0) {
            const foundPages = /[&?]page=([0-9]+)(&|$)/i.exec(iri.substring(paramsStart));
            // parseInt no fallará porque la expresión regular coge solo números
            return foundPages && foundPages.length > 1 ? parseInt(foundPages[1], 10) : null;
        }
    }

    /**
     * Indica si es la última página
     */
    public isLastPage(): boolean {
        return (this.iriCurrentPage && this.iriCurrentPage === this.iriLastPage) || this.totalItems === this.member.length;
    }

    /**
     * Indica si puede ser que estemos en / nos hayamos pasado la última página,
     * también devuelve true si no sabemos en qué página estamos o cual es la última.
     */
    public canBeInOrOverLastPage(): boolean {
        return this.currentPage === undefined || this.lastPage === undefined || this.currentPage >= this.lastPage;
    }

    public clone<V>(func: (x: T) => V): Paginated<V> {
        const paginatedAnswer = {
            'hydra:member': this.member.map(func),
            'hydra:totalItems': this.totalItems,
        };
        if (this.iriCurrentPage) {
            paginatedAnswer['hydra:view'] = {
                '@id': this.iriCurrentPage,
                'hydra:first': this.iriFirstPage,
                'hydra:next': this.iriNextPage,
                'hydra:previous': this.iriPrevPage,
                'hydra:last': this.iriLastPage
            };
        }
        return new Paginated<V>(paginatedAnswer);
    }

    [Symbol.iterator](): Iterator<T> {
        return this.member.values();
    }
}
